import { XMarkIcon, PencilSquareIcon, VideoCameraIcon, DocumentIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

const UploadedFilesList = ({ files, onEditFile, onRemoveFile }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (files.length === 0) return null

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Attached Files ({files.length})
      </label>
      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
        {files.map((file, index) => (
          <div
            key={file.id}
            className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${file.isUploading ? 'opacity-60' : ''}`}
          >
            <div className="flex items-center gap-3 p-3">
              {file.preview ? (
                <div className="relative w-16 h-16 flex-shrink-0 rounded overflow-hidden">
                  {file.type.startsWith('image/') ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : file.type.startsWith('video/') ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <VideoCameraIcon className="w-8 h-8 text-white" />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <DocumentIcon className="w-8 h-8 text-white" />
                    </div>
                  )}
                  {file.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              ) : file.type === 'application/pdf' ? (
                <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-red-50 dark:bg-red-900/20 rounded relative">
                  <DocumentTextIcon className="w-8 h-8 text-red-500" />
                  {file.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded relative">
                  <DocumentIcon className="w-8 h-8 text-gray-500" />
                  {file.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.name}
                  </p>
                  {file.isUploading && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">Uploading...</span>
                  )}
                  {file.uploadFailed && (
                    <span className="text-xs text-red-600 dark:text-red-400">Failed</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </p>
                  {file.trimDuration && file.trimDuration > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-[#25D366]/20 text-[#075E54] dark:text-[#25D366] rounded">
                      Trimmed: {Math.floor(file.trimDuration)}s
                    </span>
                  )}
                </div>
                {file.caption && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic truncate">
                    &quot;{file.caption}&quot;
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => onEditFile(index)}
                  disabled={file.isUploading}
                  className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit file"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onRemoveFile(file.id)}
                  disabled={file.isUploading}
                  className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove file"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default UploadedFilesList

